import { useId, type ComponentPropsWithoutRef } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { useGlossaryHighlight } from "@/components/glossary/GlossaryHighlightContext";
import { GlossaryTermMark } from "@/components/glossary/GlossaryTermMark";
import StoryboardLink from "@/components/storyboard/StoryboardLink";
import { rehypeGlossary, GLOSSARY_TERM_PROP } from "@/lib/glossaryHighlight";

const headingRemap: Components = {
  h1: ({ children }: ComponentPropsWithoutRef<"h1">) => <h2>{children}</h2>,
  h2: ({ children }: ComponentPropsWithoutRef<"h2">) => <h3>{children}</h3>,
  h3: ({ children }: ComponentPropsWithoutRef<"h3">) => <h4>{children}</h4>,
  h4: ({ children }: ComponentPropsWithoutRef<"h4">) => <h5>{children}</h5>,
  h5: ({ children }: ComponentPropsWithoutRef<"h5">) => <h6>{children}</h6>,
};

// The rehypeGlossary plugin wraps first-occurrence terms in a <span> carrying
// the term id; this override turns those into interactive term markers while
// leaving any other span untouched.
const glossarySpan: Components["span"] = ({ node, children }) => {
  const termId = node?.properties?.[GLOSSARY_TERM_PROP];
  if (typeof termId === "string") {
    return <GlossaryTermMark termId={termId}>{children}</GlossaryTermMark>;
  }
  return <span>{children}</span>;
};

const glossaryComponents: Components = { span: glossarySpan };

// Content Factory carries an author-set width in the markdown image title slot,
// as a % of the text column. That slot is a carrier only - it never holds an
// author tooltip, so it is never forwarded to the DOM.
const WIDTH_TITLE = /^width=(\d{1,3}%)$/;

// Unsized images are capped, not stretched; 640px matches the editor.
const boundedImage: Components["img"] = ({ src, alt, title }) => {
  const width = WIDTH_TITLE.exec(title ?? "")?.[1];

  return (
    <img
      src={src}
      alt={alt ?? ""}
      className={width ? "block h-auto" : "h-auto max-w-[min(100%,640px)]"}
      style={width ? { width } : undefined}
    />
  );
};

export const imageSizing: Components = { img: boundedImage };

// react-markdown passes a hast `node` this anchor does not render.
// StoryboardLink reads the title carrier and drops it, the same way image
// width is dropped.
const markdownAnchor: Components["a"] = ({
  href,
  title,
  children,
  className,
}) => (
  <StoryboardLink href={href} title={title} className={className}>
    {children}
  </StoryboardLink>
);

const storyboardLinks: Components = { a: markdownAnchor };

/**
 * Strip leading/trailing horizontal rules (---, ***, ___) from markdown.
 * API-delivered markdown sometimes starts or ends with hr patterns.
 */
export const stripBorderHrs = (text: string): string =>
  (text ?? "")
    .replace(/^[ \t]*([-*_][ \t]*){3,}[ \t]*\n/, "")
    .replace(/\n[ \t]*([-*_][ \t]*){3,}[ \t]*$/, "");

type CourseMarkdownProps = {
  children: string;
  components?: Components;
};

export const CourseMarkdown = ({
  children,
  components,
}: CourseMarkdownProps) => {
  const glossary = useGlossaryHighlight();
  // Stable per-block id: lets the claims registry attribute a term to this
  // block, so re-renders keep the mark here instead of migrating it elsewhere.
  const ownerId = useId();
  const glossaryActive = Boolean(glossary?.enabled && glossary.compiled);

  return (
    <ReactMarkdown
      rehypePlugins={
        glossaryActive
          ? [
              [
                rehypeGlossary,
                {
                  compiled: glossary!.compiled,
                  claims: glossary!.claims,
                  ownerId,
                },
              ],
            ]
          : []
      }
      components={{
        ...headingRemap,
        ...imageSizing,
        ...storyboardLinks,
        ...(glossaryActive ? glossaryComponents : {}),
        ...components,
      }}
    >
      {stripBorderHrs(children)}
    </ReactMarkdown>
  );
};
